import { InvisibleStylePart } from "../enums/invisible-style-part";
import { EPropertyType } from "../models/property-type";
import { IQuickProp, QUICK_PROPS_MAX, QuickStyleKey } from "../models/quick-prop";

/** The one property field `validateQuickProps` needs — a structural subset of `IPropertyDefinition`/`IProperty`. */
export interface IQuickPropValidationProperty {
  name: string;
  type: number[];
  isTechnical?: boolean;
  isDeprecated?: boolean;
}

/** The one component-definition shape `validateQuickProps` needs — a structural subset of `IComponentDefinition`. */
export interface IQuickPropValidationDefinition {
  name: string;
  properties: IQuickPropValidationProperty[];
  inVisibleStyles?: string[];
  childrenNames?: string[];
  quickProps?: IQuickProp[];
}

/** Why one `IQuickProp` entry failed validation. */
export type QuickPropIssueCode =
  | "tooMany"
  | "duplicate"
  | "unknownProperty"
  | "technicalProperty"
  | "deprecatedProperty"
  | "notAnEvent"
  | "eventAsProperty"
  | "styleHidden"
  | "unknownChild"
  | "emptyGroup"
  /** `kind` isn't one of the recognized discriminants — the component set was authored by a newer
   *  designer build than the one running this validator. */
  | "unknownKind"
  /** `kind: "style"`'s `key` isn't one of the recognized `QuickStyleKey` values. */
  | "unknownStyleKey"
  /** The entry isn't a plain object, or is missing/has the wrong runtime type for a field its
   *  (recognized) `kind` requires — e.g. a non-string `name`, `names`/`childNames` that isn't a
   *  string array, or an optional `command.label` / `children.addChild` that is present but not a string. */
  | "malformed";

/**
 * The issue codes that mean "this build does not understand the entry", not "the entry is wrong": a newer
 * designer build may add a `kind` or a `QuickStyleKey`. An editor must not force deleting such an entry —
 * it warns and preserves it verbatim on save. Every other code (including `"malformed"`) blocks a save.
 */
export const FORWARD_COMPAT_QUICK_PROP_ISSUE_CODES: readonly QuickPropIssueCode[] = ["unknownKind", "unknownStyleKey"];

/** Whether `code` is one of `FORWARD_COMPAT_QUICK_PROP_ISSUE_CODES` (warn-and-preserve, never blocking). */
export function isForwardCompatQuickPropIssue(code: QuickPropIssueCode): boolean {
  return FORWARD_COMPAT_QUICK_PROP_ISSUE_CODES.includes(code);
}

/** One problem found in a component definition's `quickProps`, addressed to the offending array index. */
export interface IQuickPropIssue {
  index: number;
  code: QuickPropIssueCode;
  message: string;
}

/**
 * Maps each `QuickStyleKey` to the `InvisibleStylePart` name(s) that control its visibility in the styling
 * panel. `"text.format"` maps to font-weight because that is the styling field the toolbar's bold/italic-style
 * "format" control actually edits. Its own keys are also the set of recognized `QuickStyleKey` values at
 * runtime (see `getQuickPropShapeIssue`'s `"unknownStyleKey"`), so consumers that list style candidates (the
 * component-set editor) read it instead of keeping a second copy.
 */
export const QUICK_STYLE_INVISIBLE_NAMES: Readonly<Record<QuickStyleKey, readonly string[]>> = {
  "text.fontSize": [InvisibleStylePart.Text_FontSize],
  "text.fontFamily": [InvisibleStylePart.Text_FontFamily],
  "text.color": [InvisibleStylePart.Text_Color],
  "text.format": [InvisibleStylePart.Text_FontWeight],
  "fill.color": [InvisibleStylePart.Fill_Color],
  "position.textAlign": [InvisibleStylePart.Layout_TextAlign]
};

/**
 * A style is hidden either by its own leaf name (`"text_fontSize"`) or by its whole section being hidden
 * (`"text"`) — component metadata lists both forms interchangeably (see e.g. TableRow's `inVisibleStyles`,
 * which lists `"layout_textAlign"` alongside the blanket `"layout"`).
 */
export function isQuickStyleHidden(key: QuickStyleKey, inVisibleStyles: readonly string[]): boolean {
  const names: readonly string[] = QUICK_STYLE_INVISIBLE_NAMES[key];
  return names.some((name: string): boolean => {
    if (inVisibleStyles.includes(name)) return true;
    const group: string = name.split("_")[0];
    return inVisibleStyles.includes(group);
  });
}

/**
 * A stable identity per `IQuickProp` entry: two entries with the same identity are duplicates. Only call it
 * on an entry `getQuickPropShapeIssue` accepted — an unrecognized entry has no identity of its own.
 */
export function quickPropIdentity(prop: IQuickProp): string {
  switch (prop.kind) {
    case "property":
      return `property:${prop.name}`;
    case "propertyGroup":
      return `propertyGroup:${prop.label}`;
    case "event":
      return `event:${prop.name}`;
    case "style":
      return `style:${prop.key}`;
    case "command":
      return `command:${prop.childName}`;
    case "children":
      return `children:${prop.label}`;
    default: {
      // Defensive only: callers screen out unrecognized/malformed `kind` values (see `unknownKind` /
      // `malformed`) before this is ever called — but identity must never throw on component-set data
      // that is newer than this build.
      const exhaustive: never = prop;
      return `unknown:${JSON.stringify(exhaustive)}`;
    }
  }
}

/** The `IQuickProp["kind"]` discriminants this build understands. */
const KNOWN_QUICK_PROP_KINDS: readonly IQuickProp["kind"][] = [
  "property",
  "propertyGroup",
  "event",
  "style",
  "command",
  "children"
];

/** Structural view of a quick prop entry before its `kind` has been verified as recognized — component-set
 *  data can be authored by a newer (or hand-edited) designer build, so every field starts as `unknown`. */
interface IUnknownQuickProp {
  kind?: unknown;
  name?: unknown;
  label?: unknown;
  names?: unknown;
  key?: unknown;
  childName?: unknown;
  childNames?: unknown;
  addChild?: unknown;
}

function isPlainObject(value: unknown): value is IUnknownQuickProp {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item: unknown): boolean => typeof item === "string");
}

/** An optional field is fine when absent; when present it must be a string. */
function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === "string";
}

/**
 * Checks that an entry's fields have the runtime shape its (already-recognized) `kind` requires, so the
 * rest of `validateQuickProps` (and `quickPropIdentity`) can trust `name`/`label`/`names`/`key`/`childName`/
 * `childNames` without re-checking. Returns `undefined` when the shape is fine, `"unknownStyleKey"` for a
 * `style` entry whose `key` isn't a recognized `QuickStyleKey`, or `"malformed"` for anything else wrong.
 */
function shapeIssueFor(kind: IQuickProp["kind"], raw: IUnknownQuickProp): "unknownStyleKey" | "malformed" | undefined {
  switch (kind) {
    case "property":
    case "event":
      return typeof raw.name === "string" ? undefined : "malformed";
    case "propertyGroup":
      return typeof raw.label === "string" && isStringArray(raw.names) ? undefined : "malformed";
    case "style":
      if (typeof raw.key !== "string") return "malformed";
      // `in` also matches inherited `Object.prototype` names (`"toString"`, `"constructor"`, ...) —
      // `hasOwnProperty` keeps a key like that from slipping through as if it were a real `QuickStyleKey`
      // and later blowing up `QUICK_STYLE_INVISIBLE_NAMES[key].some(...)` on a non-array prototype method.
      return Object.prototype.hasOwnProperty.call(QUICK_STYLE_INVISIBLE_NAMES, raw.key) ? undefined : "unknownStyleKey";
    case "command":
      return typeof raw.childName === "string" && isOptionalString(raw.label) ? undefined : "malformed";
    case "children":
      return typeof raw.label === "string" && isStringArray(raw.childNames) && isOptionalString(raw.addChild)
        ? undefined
        : "malformed";
  }
}

/** One raw quick prop entry's shape problem, if any — mirrors the three defensive `QuickPropIssueCode`
 *  members (`"unknownKind"`, `"unknownStyleKey"`, `"malformed"`). */
export type QuickPropShapeIssue = "unknownKind" | "unknownStyleKey" | "malformed";

/**
 * Classifies one RAW quick prop entry — untyped, unvalidated component-set data — without ever throwing:
 * `"malformed"` when it isn't even a plain object (or is missing/has the wrong runtime type for a field its
 * `kind` requires), `"unknownKind"` when `kind` isn't one of the six discriminants this build knows, or
 * `"unknownStyleKey"` when a `style` entry's `key` isn't a recognized `QuickStyleKey`. Returns `undefined`
 * when the entry is well-formed enough to treat as an `IQuickProp`.
 *
 * `validateQuickProps` uses this internally; it is also exported for callers that touch quick prop entries
 * directly — outside a full validation pass, e.g. the component-set editor's toolbar list — so they can
 * skip or label a bad entry the same way instead of indexing into it and crashing.
 */
export function getQuickPropShapeIssue(entry: unknown): QuickPropShapeIssue | undefined {
  if (!isPlainObject(entry)) return "malformed";
  if (typeof entry.kind !== "string" || !(KNOWN_QUICK_PROP_KINDS as readonly string[]).includes(entry.kind)) {
    return "unknownKind";
  }
  return shapeIssueFor(entry.kind as IQuickProp["kind"], entry);
}

/** Builds the user-facing message for a `getQuickPropShapeIssue` result — kept next to it so the two never
 *  drift on what each code means. */
function shapeIssueMessage(shapeIssue: QuickPropShapeIssue, index: number, defName: string, raw: unknown): string {
  const detail: IUnknownQuickProp | undefined = isPlainObject(raw) ? raw : undefined;
  if (shapeIssue === "unknownKind") {
    return `Quick prop entry at index ${index} on "${defName}" has an unrecognized kind (${JSON.stringify(detail?.kind)}).`;
  }
  if (shapeIssue === "unknownStyleKey") {
    return `Quick prop style entry at index ${index} on "${defName}" has an unrecognized style key (${JSON.stringify(detail?.key)}).`;
  }
  return `Quick prop entry at index ${index} on "${defName}" is malformed and could not be validated.`;
}

/**
 * Validates a `"property"` / `"propertyGroup"` member name against the definition's property list: it must
 * exist, must not be event-typed (events belong to `kind: "event"` instead), must not be technical, and must
 * not be deprecated.
 */
function checkPropertyRef(
  name: string,
  defName: string,
  index: number,
  propertyByName: Map<string, IQuickPropValidationProperty>,
  issues: IQuickPropIssue[]
): void {
  const property: IQuickPropValidationProperty | undefined = propertyByName.get(name);
  if (!property) {
    issues.push({
      index,
      code: "unknownProperty",
      message: `Quick prop references unknown property "${name}" on "${defName}".`
    });
    return;
  }
  if (property.type.includes(EPropertyType.event)) {
    issues.push({
      index,
      code: "eventAsProperty",
      message: `Quick prop "${name}" on "${defName}" is an event property; use kind "event" instead of "property"/"propertyGroup".`
    });
    return;
  }
  if (property.isTechnical) {
    issues.push({
      index,
      code: "technicalProperty",
      message: `Quick prop references technical property "${name}" on "${defName}".`
    });
    return;
  }
  if (property.isDeprecated) {
    issues.push({
      index,
      code: "deprecatedProperty",
      message: `Quick prop references deprecated property "${name}" on "${defName}".`
    });
  }
}

/**
 * Validates a component definition's `quickProps` against the rest of the definition: the `QUICK_PROPS_MAX`
 * bound, duplicate entries, dangling property/event/child references, technical/deprecated/event-typed
 * properties used as plain properties, and styles hidden by `inVisibleStyles`. Returns an empty array when
 * `quickProps` is absent, empty, or fully valid.
 */
export function validateQuickProps(def: IQuickPropValidationDefinition): IQuickPropIssue[] {
  // `def.quickProps` is typed `IQuickProp[] | undefined`, but this is stored JSON that can be hand-edited
  // or authored by a build newer than this one — guard the array shape itself, not just its entries.
  const quickProps: IQuickProp[] = Array.isArray(def.quickProps) ? def.quickProps : [];
  const issues: IQuickPropIssue[] = [];

  if (quickProps.length > QUICK_PROPS_MAX) {
    issues.push({
      index: QUICK_PROPS_MAX,
      code: "tooMany",
      message: `"${def.name}" declares ${quickProps.length} quick props; the max is ${QUICK_PROPS_MAX}.`
    });
  }

  const propertyByName: Map<string, IQuickPropValidationProperty> = new Map(
    def.properties.map((property: IQuickPropValidationProperty): [string, IQuickPropValidationProperty] => [
      property.name,
      property
    ])
  );
  const inVisibleStyles: readonly string[] = def.inVisibleStyles ?? [];
  const childrenNames: readonly string[] = def.childrenNames ?? [];
  const seenIdentities: Set<string> = new Set<string>();

  quickProps.forEach((entry: IQuickProp, index: number): void => {
    // `entry` is typed `IQuickProp`, but at runtime this is stored JSON: it may not actually be an
    // object, may carry a `kind` this build doesn't recognize (newer designer), or may be missing/have
    // the wrong runtime type for a field its `kind` requires. Screen all of that out here, as an issue,
    // before trusting the discriminated-union shape below — the validator must never throw on bad data.
    const shapeIssue: QuickPropShapeIssue | undefined = getQuickPropShapeIssue(entry);
    if (shapeIssue !== undefined) {
      issues.push({ index, code: shapeIssue, message: shapeIssueMessage(shapeIssue, index, def.name, entry) });
      return;
    }

    const prop: IQuickProp = entry;
    const identity: string = quickPropIdentity(prop);
    if (seenIdentities.has(identity)) {
      issues.push({ index, code: "duplicate", message: `Duplicate quick prop entry on "${def.name}" (${identity}).` });
    } else {
      seenIdentities.add(identity);
    }

    switch (prop.kind) {
      case "property": {
        checkPropertyRef(prop.name, def.name, index, propertyByName, issues);
        break;
      }
      case "propertyGroup": {
        if (prop.names.length === 0) {
          issues.push({
            index,
            code: "emptyGroup",
            message: `Quick prop group "${prop.label}" on "${def.name}" has no member properties.`
          });
        }
        prop.names.forEach((name: string): void => {
          checkPropertyRef(name, def.name, index, propertyByName, issues);
        });
        break;
      }
      case "event": {
        const property: IQuickPropValidationProperty | undefined = propertyByName.get(prop.name);
        if (!property) {
          issues.push({
            index,
            code: "unknownProperty",
            message: `Quick prop event references unknown property "${prop.name}" on "${def.name}".`
          });
        } else if (!property.type.includes(EPropertyType.event)) {
          issues.push({
            index,
            code: "notAnEvent",
            message: `Quick prop "${prop.name}" on "${def.name}" is not an event property.`
          });
        }
        break;
      }
      case "style": {
        if (isQuickStyleHidden(prop.key, inVisibleStyles)) {
          issues.push({
            index,
            code: "styleHidden",
            message: `Quick prop style "${prop.key}" is hidden by inVisibleStyles on "${def.name}".`
          });
        }
        break;
      }
      case "command": {
        if (!childrenNames.includes(prop.childName)) {
          issues.push({
            index,
            code: "unknownChild",
            message: `Quick prop command targets unknown child "${prop.childName}" on "${def.name}".`
          });
        }
        break;
      }
      case "children": {
        if (prop.childNames.length === 0) {
          issues.push({
            index,
            code: "emptyGroup",
            message: `Quick prop children group "${prop.label}" on "${def.name}" has no child names.`
          });
        }
        // `addChild` is checked against the group's OWN `childNames`, not `def.childrenNames`: a children
        // group lists descendants (a Table's columns live under its TableRow), so the component's direct
        // children are the wrong reference — but an add command for a child the group doesn't show is.
        if (prop.addChild !== undefined && !prop.childNames.includes(prop.addChild)) {
          issues.push({
            index,
            code: "unknownChild",
            message: `Quick prop children group "${prop.label}" on "${def.name}" adds "${prop.addChild}", which is not one of its child names.`
          });
        }
        break;
      }
      default: {
        // Defensive only: `kind` was already checked against `KNOWN_QUICK_PROP_KINDS` above (and
        // reported as `unknownKind` when it didn't match) — this branch never actually runs, but must
        // not throw either.
        const exhaustive: never = prop;
        void exhaustive;
      }
    }
  });

  return issues;
}
